
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FiUser, FiBarChart2, FiUsers } from 'react-icons/fi';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import { Loader } from '../components/Common/Loader';
import { ErrorDisplay } from '../components/Common/ErrorDisplay';
import api from '../api/client';
import { useAuthStore } from '../stores';

const FollowersPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuthStore();
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    // Fetch master details (using current user ID) to get followers
    const { data, isLoading, error } = useQuery({
        queryKey: ['masterDetails', user?._id],
        queryFn: async () => {
            if (!user?._id) return null;
            const response = await api.get(`/master-details/${user._id}`);
            return response.data;
        },
        enabled: !!user?._id && user?.role === 'master',
        staleTime: 3 * 60 * 1000,
    });

    const followers = data?.masterDetails?.followers || [];

    const handleLogout = () => {
        logout();
        window.location.href = '/login';
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                {user && <Header user={user} onLogout={handleLogout} showSidebarToggle onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />}
                <div className="flex relative w-full max-w-full pt-0 lg:pt-0">
                    <Sidebar
                        activeTab="followers"
                        setActiveTab={() => { }}
                        user={user!}
                        isOpen={isSidebarOpen}
                        onClose={() => setSidebarOpen(false)}
                    />
                    <main className="flex-1 min-w-0 max-w-full px-3 py-4 sm:px-6 lg:px-6 lg:ml-72 overflow-x-hidden">
                        <Loader message="Loading followers..." />
                    </main>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                {user && <Header user={user} onLogout={handleLogout} showSidebarToggle onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />}
                <div className="flex relative w-full max-w-full pt-0 lg:pt-0">
                    <Sidebar
                        activeTab="followers"
                        setActiveTab={() => { }}
                        user={user!}
                        isOpen={isSidebarOpen}
                        onClose={() => setSidebarOpen(false)}
                    />
                    <main className="flex-1 min-w-0 max-w-full px-3 py-4 sm:px-6 lg:px-6 lg:ml-72 overflow-x-hidden">
                        <ErrorDisplay error="Failed to load followers" />
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {user && <Header user={user} onLogout={handleLogout} showSidebarToggle onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />}
            <div className="flex relative w-full max-w-full pt-0 lg:pt-0">
                <Sidebar
                    activeTab="followers"
                    setActiveTab={() => { }}
                    user={user!}
                    isOpen={isSidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                />

                {isSidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-20 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                <main className="flex-1 min-w-0 max-w-full px-3 py-4 sm:px-6 sm:py-8 lg:px-8 lg:ml-72 overflow-x-hidden">
                    <div className="max-w-7xl mx-auto space-y-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-full">
                                <FiUsers className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div className="min-w-0">
                                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Followers</h1>
                                <p className="text-gray-600 dark:text-gray-400">Manage and view statistics of your copy traders (active and paused)</p>
                            </div>
                        </div>

                        {followers.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                                {followers.map((follower: any) => (
                                    <div key={follower._id} className="card min-w-0 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-500 transition-colors p-4 sm:p-6 rounded-xl shadow-sm">
                                        <div className="flex items-center gap-4 mb-6">
                                            <div className="p-4 shrink-0 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                                                <FiUser className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h4 className="font-bold text-lg text-gray-900 dark:text-white break-anywhere">{follower.name}</h4>
                                                    <span
                                                        className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                                            follower.status === 'paused'
                                                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                                                : follower.status === 'pending'
                                                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                                                : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                                        }`}
                                                    >
                                                        {follower.status === 'paused'
                                                            ? 'Paused'
                                                            : follower.status === 'pending'
                                                            ? 'Pending'
                                                            : 'Active'}
                                                    </span>
                                                </div>
                                                {/* <p className="text-sm text-gray-500 dark:text-gray-400">{follower.email}</p> */}
                                            </div>
                                        </div>

                                        <div className="space-y-3 text-sm mb-6">
                                            <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <span className="text-gray-600 dark:text-gray-400">Allocation</span>
                                                <span className="font-bold text-primary-600 dark:text-primary-400">{follower.allocationPercent}%</span>
                                            </div>
                                            <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <span className="text-gray-600 dark:text-gray-400">Max Investment</span>
                                                <span className="font-semibold text-gray-900 dark:text-white">
                                                    ${follower.maxInvestmentAmount?.toLocaleString() || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <span className="text-gray-600 dark:text-gray-400">Balance</span>
                                                <span className="font-semibold text-gray-900 dark:text-white">
                                                    ${follower.accountBalance?.toLocaleString() || '0'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <span className="text-gray-600 dark:text-gray-400">Since</span>
                                                <span className="font-medium text-gray-900 dark:text-white">
                                                    {new Date(follower.startDate).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => navigate(`/dashboard/statistics?userid=${follower._id}`)}
                                            className="w-full py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                                        >
                                            <FiBarChart2 className="h-4 w-4" />
                                            View Statistics
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                                <FiUsers className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No Followers Yet</h3>
                                <p className="text-gray-500 dark:text-gray-400">When users start copying your trades, they will appear here.</p>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default FollowersPage;
